import React, { useEffect, useState } from 'react';
import { X, Plus, Minus, Trash2, CreditCard, Package, Search, ShoppingCart, User, Calculator, Tag } from 'lucide-react';
import {useQuickCheckoutData} from "../../components/quickCheckoutScreen/useQuickCheckoutData";
import {useBusinessPolicies} from "../../../settings/businessPolicies/useBusinessPolicies";
import {toast} from "react-toastify";
import { quickCheckout, getSale } from "../../../redux/salesSlice";
import {useAppDispatch} from "../../../redux/store";
import PaymentModal from '../ui/PaymentModal';
import { clientSearchOverflowMessage, fetchClientSearchResults } from "../../../redux/clientsSlice";

interface QuickCheckoutItem {
    id: number;
    type: 'service' | 'product';
    name: string;
    price: number;
    quantity: number;
    total: number;
    category?: string;
    practitioner?: string;
}

interface QuickCheckoutModalProps {
    onClose: () => void;
    onComplete: (checkoutData: any) => void;
    clientData?: {
        name: string;
        email: string;
        phone?: string;
        cardOnFile?: {
            brand: string;
            last4: string;
            expiry: string;
        };
    };
    taxConfig?: {
        applyToServices: boolean;
        applyToProducts: boolean;
        rate: number;
    };
}

const QuickCheckoutModal: React.FC<QuickCheckoutModalProps> = ({
                                                                   onClose,
                                                                   onComplete,
                                                                   clientData,

                                                               }) => {
    const dispatch = useAppDispatch();
    const [createdSale, setCreatedSale] = useState<any>(null);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [items, setItems] = useState<QuickCheckoutItem[]>([]);
    const {
        clients,
        staff,
        products
    } = useQuickCheckoutData();
    const {
        account,
    } = useBusinessPolicies();
    const [taxConfig, setTaxConfig] = React.useState<{
        applyToServices: boolean;
        applyToProducts: boolean;
        rate: number;
    }>(
         {
            applyToServices: account.applyTaxToService,
            applyToProducts: account.applyTaxToProduct,
            rate: account?.shops[0]?.taxPercentage ?? 0
        }
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [clientSearchTerm, setClientSearchTerm] = useState('');
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [activeTab, setActiveTab] = useState<'services' | 'products'>('services');
    const [selectedClient, setSelectedClient] = useState<any>(null);
    const [assignedStaff, setAssignedStaff] = useState<number | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<'card' | 'card_on_file' | 'cash' | 'manual_entry'>('card');
    const [tipAmount, setTipAmount] = useState(0);
    const [tipRecipient, setTipRecipient] = useState('');
    const [discount, setDiscount] = useState(0);
    const [discountType, setDiscountType] = useState<"percentage" | "fixed">("percentage");
    const [showDiscountInput, setShowDiscountInput] = useState(false);
    const [clientSearchResults, setClientSearchResults] = useState<typeof clients | null>(null);
    const [isSearchingClients, setIsSearchingClients] = useState(false);
    const [clientSearchMessage, setClientSearchMessage] = useState<string | null>(null);

    // Search the server so every client is reachable, not just the page preloaded into redux
    useEffect(() => {
        const term = clientSearchTerm.trim();
        if (!term) {
            setClientSearchResults(null);
            setClientSearchMessage(null);
            setIsSearchingClients(false);
            return;
        }

        let cancelled = false;
        setIsSearchingClients(true);
        const handler = setTimeout(() => {
            fetchClientSearchResults(term)
                .then(({ clients: results, totalCount }) => {
                    if (cancelled) return;
                    setClientSearchMessage(clientSearchOverflowMessage(results.length, totalCount));
                    setClientSearchResults(results.map((c) => ({
                        id: c.id,
                        name: c.name,
                        email: c.email,
                        phone: c.phone,
                    })));
                })
                .catch((error) => {
                    if (cancelled) return;
                    console.error("Error searching clients:", error);
                    setClientSearchResults([]);
                    setClientSearchMessage(null);
                })
                .finally(() => {
                    if (!cancelled) setIsSearchingClients(false);
                });
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(handler);
        };
    }, [clientSearchTerm]);

    const filteredClients = clientSearchResults ?? clients;

    const addItem = (item: any) => {
        if (activeTab === "services") {

            if (!assignedStaff) {
                toast.warning("Please select staff first");
                return;
            }

            const selectedStaffObject = staff.find(
                (s) => s.id === assignedStaff
            );

            if (!selectedStaffObject) return;

            const merchantService = selectedStaffObject.services.find(
                (ms: any) => ms.id === item.id
            );

            if (!merchantService) return;

            const alreadyAdded = items.find(
                (i) => i.id === merchantService.id && i.type === "service"
            );

            if (alreadyAdded) return;

            const price = (merchantService.priceCents ?? 0) / 100;

            const newItem: QuickCheckoutItem = {
                id: merchantService.id,
                type: "service",
                name: merchantService.businessService?.title || merchantService.title,
                price,
                quantity: 1,
                total: price,
                category: merchantService.businessService?.category?.name,
            };

            setItems(prev => [...prev, newItem]);

            return;
        }

        const existingItem = items.find((i) => i.id === item.id);

        if (existingItem) {
            updateQuantity(item.id, existingItem.quantity + 1);
            return;
        }

        const price = (item.priceCents ?? 0) / 100;

        const newItem: QuickCheckoutItem = {
            id: item.id,
            type: "product",
            name: item.title,
            price,
            quantity: 1,
            total: price,
            category: item.category,
        };

        setItems(prev => [...prev, newItem]);
    };

    const updateQuantity = (itemId: number, quantity: number) => {
        if (quantity <= 0) {
            removeItem(itemId);
            return;
        }
        setItems(prev => prev.map(item =>
            item.id === itemId
                ? { ...item, quantity, total: item.price * quantity }
                : item
        ));
    };

    const removeItem = (itemId: number) => {
        setItems(prev => prev.filter(item => item.id !== itemId));
    };

    const updatePractitioner = (itemId: number, practitioner: string) => {
        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, practitioner } : item
        ));
    };

    const calculateSubtotal = () => {
        return items.reduce((sum, item) => sum + item.total, 0);
    };

    const calculateTax = () => {
        const serviceTotal = items
            .filter(item => item.type === 'service')
            .reduce((sum, item) => sum + item.total, 0);

        const productTotal = items
            .filter(item => item.type === 'product')
            .reduce((sum, item) => sum + item.total, 0);

        let taxableAmount = 0;

        if (taxConfig.applyToServices) {
            taxableAmount += serviceTotal;
        }

        if (taxConfig.applyToProducts) {
            taxableAmount += productTotal;
        }

        return taxableAmount * (taxConfig.rate / 100);
    };

    const calculateDiscountAmount = () => {
        if (discountType === 'percentage') {
            return (calculateSubtotal() + calculateTax()) * (discount / 100);
        }
        return discount;
    };

    const calculateTotal = () => {
        return Math.max(0, calculateSubtotal() + calculateTax() - calculateDiscountAmount());
    };

    const selectedStaffObject = staff.find(s => s.id === assignedStaff);

    const availableServicesForStaff =
        selectedStaffObject?.services || [];

    const filteredItems =
        (activeTab === "services"
                ? availableServicesForStaff
                : products
        ).filter((item: any) => {
            const label = (item.title || "").toLowerCase();
            const category = (item.category || "").toLowerCase();
            const search = searchTerm.toLowerCase();
            return label.includes(search) || category.includes(search);
        });

    const handleQuickCheckout = async () => {
        if (items.length === 0) {
            toast.warning("Please add at least one item");
            return;
        }

        try {
            const serviceIds = items
                .filter((i) => i.type === "service")
                .map((i) => i.id);

            const products = items
                .filter((i) => i.type === "product")
                .map((i) => ({
                    productId: i.id,
                    quantity: i.quantity,
                }));

            const result = await dispatch(
                quickCheckout({
                    clientId: selectedClient?.id,
                    staffId: assignedStaff,
                    serviceIds,
                    products,
                })
            ).unwrap();

            setCreatedSale(result);
            setPaymentOpen(true);

        } catch (err) {
            console.error("Quick checkout failed", err);
            toast.error("Checkout failed");
        }
    };
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {/* WebkitOverflowScrolling + pan-y: iPad Safari needs these for nested scroll areas */}
            {/* Full screen on phones; centered dialog from md up */}
            <div className="bg-white shadow-xl w-full h-full max-w-6xl overflow-y-auto md:h-auto md:max-h-[90vh] md:mx-4 md:rounded-lg"
                 style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y", overscrollBehavior: "contain" }}>
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <ShoppingCart className="w-6 h-6 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Quick Checkout</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Phones stack items above the cart and scroll as one page; md+ shows them side by side */}
                <div className="flex flex-col md:flex-row md:h-[calc(90vh-100px)]">
                    {/* Left Panel - Items Selection (own scroll context so the list scrolls on iPad) */}
                    <div className="md:flex-1 min-w-0 p-4 border-b md:border-b-0 md:border-r border-gray-200 md:overflow-y-auto"
                         style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}>
                        <div className="space-y-3">
                            {/* Client Selection */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="relative">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Client
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search name, phone or email"
                                            value={selectedClient?.name || clientSearchTerm}
                                            onChange={(e) => {
                                                setSelectedClient(e.target.value);
                                                setClientSearchTerm(e.target.value);
                                                setShowClientDropdown(true);
                                            }}
                                            onFocus={() => setShowClientDropdown(true)}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <Search className="w-4 h-4 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    </div>

                                    {showClientDropdown && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-32 overflow-y-auto">
                                            <div
                                                className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                                onClick={() => {
                                                    setSelectedClient('');
                                                    setClientSearchTerm('');
                                                    setShowClientDropdown(false);
                                                }}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <User className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-600">Walk-in Customer</span>
                                                </div>
                                            </div>
                                            {!isSearchingClients && clientSearchResults && clientSearchMessage && (
                                                <div className="px-2 py-1.5 text-gray-500 text-xs border-b border-gray-100">
                                                    {clientSearchMessage}
                                                </div>
                                            )}
                                            {filteredClients.map((client) => (
                                                <div
                                                    key={client.id}
                                                    className="px-2 py-1.5 hover:bg-gray-50 cursor-pointer"
                                                    onClick={() => {
                                                        setSelectedClient(client);
                                                        setClientSearchTerm(client.name);
                                                        setShowClientDropdown(false);
                                                    }}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <User className="w-4 h-4 text-blue-500" />
                                                        <span className="text-sm">{client.name}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            {isSearchingClients && (
                                                <div className="px-2 py-1.5 text-gray-500 text-xs">
                                                    Searching...
                                                </div>
                                            )}
                                            {!isSearchingClients && filteredClients.length === 0 && clientSearchTerm && (
                                                <div className="px-2 py-1.5 text-gray-500 text-xs">
                                                    No clients found
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Staff Reference
                                    </label>
                                    <select
                                        value={assignedStaff ?? ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setAssignedStaff(value ? Number(value) : null);
                                            setItems((prev) => prev.filter((item) => item.type !== "service"));
                                        }}                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select staff member</option>
                                        {staff.map((member) => (
                                            <option key={member.id} value={member.id}>
                                                {member.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search services and products..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            {/* Tabs */}
                            <div className="flex space-x-1 bg-gray-100 rounded-lg p-0.5">
                                <button
                                    onClick={() => setActiveTab('services')}
                                    className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                                        activeTab === 'services'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Services
                                </button>
                                <button
                                    onClick={() => setActiveTab('products')}
                                    className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                                        activeTab === 'products'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Products
                                </button>
                            </div>

                            {/* Items Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">

                                {activeTab === "services" && !assignedStaff && (
                                    <div className="col-span-2 flex flex-col items-center justify-center py-10 text-gray-500">
                                        <User className="w-8 h-8 mb-2 text-gray-400" />
                                        <p className="text-sm">Select a staff member to view services</p>
                                    </div>
                                )}

                                {activeTab === "services" && assignedStaff && filteredItems.length === 0 && (
                                    <div className="col-span-2 flex flex-col items-center justify-center py-10 text-gray-500">
                                        <p className="text-sm">No services available for this staff</p>
                                    </div>
                                )}

                                {filteredItems.map((item) => {
                                    const title = item.title || item.businessService?.title;

                                    return (
                                        <div
                                            key={item.id}
                                            className="border border-gray-200 rounded-lg p-2 hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => addItem(item)}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h4 className="text-xs font-medium text-gray-900">{title}</h4>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-xs font-semibold text-gray-900">
                                                        ${(item.priceCents / 100).toFixed(2)}
                                                    </p>
                                                    <button className="mt-0.5 p-1 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200">
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Cart & Checkout */}
                    <div className="w-full md:w-80 md:shrink-0 p-4 bg-gray-50">
                        <div className="space-y-4">
                            {/* Cart Items */}
                            <div>
                                <h3 className="text-base font-medium text-gray-900 mb-2">Cart ({items.length})</h3>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                    {items.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500">
                                            <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                            <p className="text-xs">No items added yet</p>
                                        </div>
                                    ) : (
                                        items.map((item) => (
                                            <div key={item.id} className="bg-white rounded-md p-2 border border-gray-200">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-medium text-gray-900 text-xs truncate">{item.name}</h4>
                                                    </div>
                                                    <div className="flex items-center space-x-2 ml-2">

                                                        {/* Quantity Controls ONLY for products */}
                                                        {item.type === "product" ? (
                                                            <div className="flex items-center space-x-1">
                                                                <button
                                                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                                    className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 border border-gray-300 rounded"
                                                                >
                                                                    <Minus className="w-2 h-2" />
                                                                </button>

                                                                <span className="text-xs font-medium w-4 text-center">
        {item.quantity}
      </span>

                                                                <button
                                                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                                    className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 border border-gray-300 rounded"
                                                                >
                                                                    <Plus className="w-2 h-2" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            /* Services → fixed quantity 1 */
                                                            <span className="text-xs font-medium w-4 text-center">
      1
    </span>
                                                        )}

                                                        {/* Total */}
                                                        <span className="font-medium text-xs text-gray-900 min-w-0">
    ${Number(item.total).toFixed(2)}
  </span>

                                                        {/* Remove */}
                                                        <button
                                                            onClick={() => removeItem(item.id)}
                                                            className="text-red-400 hover:text-red-600"
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>

                                                    </div>                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Payment Method */}
                            {/* Totals */}
                            {items.length > 0 && (
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs">
                                            <span className="text-gray-600">Subtotal:</span>
                                            <span className="font-medium">${calculateSubtotal().toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                            {(taxConfig.applyToServices || taxConfig.applyToProducts) && (
                                                <>
                                                    <span className="text-gray-600">
                                                    Tax ({taxConfig.rate}%{
                                                        !taxConfig.applyToServices && taxConfig.applyToProducts
                                                            ? ' - Products only'
                                                            : taxConfig.applyToServices && !taxConfig.applyToProducts
                                                                ? ' - Services only'
                                                                : ''
                                                    }):
                                                </span>
                                                    <span className="font-medium">${calculateTax().toFixed(2)}</span>
                                                </>
                                            )}
                                        </div>
                                        {!showDiscountInput && discount === 0 ? (
                                            <button
                                                onClick={() => setShowDiscountInput(true)}
                                                className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                                            >
                                                <Tag className="w-3.5 h-3.5" />
                                                <span className="font-medium">Add Discount</span>
                                            </button>
                                        ) : (
                                            <div className="space-y-2 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-1.5">
                                                        <Tag className="w-3.5 h-3.5 text-blue-600" />
                                                        <span className="text-xs font-semibold text-gray-700">Discount</span>
                                                    </div>
                                                    {discount > 0 && (
                                                        <button
                                                            onClick={() => {
                                                                setDiscount(0);
                                                                setShowDiscountInput(false);
                                                            }}
                                                            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 flex items-center gap-1.5 bg-white rounded-lg border border-blue-200 overflow-hidden">
                                                        <div className="flex border-r border-blue-200">
                                                            <button
                                                                onClick={() => setDiscountType('fixed')}
                                                                className={`px-2.5 py-1.5 text-xs font-semibold transition-all ${
                                                                    discountType === 'fixed'
                                                                        ? 'bg-blue-600 text-white'
                                                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                $
                                                            </button>
                                                            <button
                                                                onClick={() => setDiscountType('percentage')}
                                                                className={`px-2.5 py-1.5 text-xs font-semibold transition-all ${
                                                                    discountType === 'percentage'
                                                                        ? 'bg-blue-600 text-white'
                                                                        : 'bg-white text-gray-600 hover:bg-gray-50'
                                                                }`}
                                                            >
                                                                %
                                                            </button>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={discountType === 'percentage' ? 100 : undefined}
                                                            step={discountType === 'percentage' ? 1 : 0.01}
                                                            value={discount || ''}
                                                            onChange={(e) => {
                                                                const value = parseFloat(e.target.value) || 0;
                                                                if (discountType === 'percentage') {
                                                                    setDiscount(Math.min(100, Math.max(0, value)));
                                                                } else {
                                                                    setDiscount(Math.max(0, value));
                                                                }
                                                            }}
                                                            placeholder="0"
                                                            className="flex-1 px-2 py-1.5 text-sm text-right border-0 focus:ring-0 focus:outline-none"
                                                        />
                                                    </div>
                                                </div>

                                                {discountType === 'percentage' && (
                                                    <div className="flex gap-1.5">
                                                        {[10, 20, 50].map((preset) => (
                                                            <button
                                                                key={preset}
                                                                onClick={() => setDiscount(preset)}
                                                                className={`flex-1 py-1 text-xs font-medium rounded transition-all ${
                                                                    discount === preset
                                                                        ? 'bg-blue-600 text-white shadow-sm'
                                                                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-blue-200'
                                                                }`}
                                                            >
                                                                {preset}%
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}

                                                {discount > 0 && (
                                                    <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                                                        <span className="text-xs text-gray-600">Discount Amount:</span>
                                                        <span className="text-sm font-bold text-green-600">
                              -${calculateDiscountAmount().toFixed(2)}
                            </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className="border-t border-gray-200 pt-1">
                                            <div className="flex justify-between">
                                                <span className="text-sm font-semibold text-gray-900">Total:</span>
                                                <span className="font-bold text-base text-gray-900">${calculateTotal().toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Payment Method */}
                            {items.length > 0 && (
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                    <h4 className="text-sm font-medium text-gray-900 mb-2">Payment Method</h4>
                                    <button
                                        onClick={handleQuickCheckout}
                                        disabled={items.length === 0}
                                        className={`w-full flex items-center justify-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                                            items.length === 0
                                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                        } disabled:opacity-50`}
                                    >
                                        <span>Complete Payment</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {createdSale && (
                <PaymentModal
                    tax={calculateTax()}
                    discount={calculateDiscountAmount()}
                    updateCalendarData={() => {}}
                    open={paymentOpen}
                    onOpenChange={setPaymentOpen}
                    sale={createdSale}
                    onPaymentSuccess={onClose}
                />
            )}
        </div>
    );
};

export default QuickCheckoutModal;